import React, { useCallback, useMemo, useRef } from "react";
import { useAskAnythingChat } from "./AskAnythingChatWrapper";
import { AskAnythingChatMessages } from "./AskAnythingChatMessages";
import styles from "./AskAnythingChat.module.scss";
import { AskAnythingChatInput } from "./AskAnythingChatInput";

interface AskAnythingChatProps {
  emptyState?: React.ReactNode;
  variant?: "default" | "expanded";
}

export function AskAnythingChat({
  emptyState,
  variant = "default",
}: AskAnythingChatProps) {
  const {
    messages = [],
    input = "",
    setInput = () => {},
    handleSubmit = () => {},
    status,
    isDisabled,
    loadingText,
  } = useAskAnythingChat();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoading = status === "submitted" || status === "streaming";

  const onSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }
      if (!input?.trim() || isLoading) return;
      handleSubmit();
    },
    [input, isLoading, handleSubmit]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit]
  );

  const showWelcome = useMemo(() => messages.length === 0, [messages.length]);
  const isExpanded = useMemo(() => variant === "expanded", [variant]);

  return (
    <div className={`${styles.wrapper} ${isExpanded ? styles.expanded : ""}`}>
      <div className={styles.scrollableContent} ref={scrollContainerRef}>
        {showWelcome && emptyState}

        <AskAnythingChatMessages
          messages={messages}
          isLoading={isLoading}
          loadingText={loadingText}
          scrollContainerRef={scrollContainerRef}
          variant={variant}
        />
      </div>

      <div className={styles.inputArea}>
        <AskAnythingChatInput
          input={input}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onChange={setInput}
          onKeyDown={onKeyDown}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
}
